/**
 * The two calendar insights whose verb does the work instead of opening
 * something (`proactiveCalendarIntelligence`):
 *
 * - A deadline alert ("Tax filing is Friday. Want a reminder before then?")
 *   sets the reminder: a Smart Manager reminder in the user's silo, never an
 *   item in a provider's calendar.
 * - A follow-up nudge ("No reply yet from Dana re: Thursday. Want to follow
 *   up?") drafts the follow-up email. The draft opens for the person to read
 *   and send; nothing is sent from the feed.
 *
 * Read from a stored row (extras lifted onto it) or a feed item (extras nested
 * under `extraFields`), so the feed presentation, the row and the action
 * routes agree on what the row offers. The routes trust only the notification
 * id: every other field comes from the stored row.
 *
 * Pure.
 */

import { calendarInsightIdentity } from "@/src/features/notifications/calendarInsightRows";

export const SET_REMINDER_LABEL = "Set reminder";
/** `metadata.generator` of a reminder a deadline alert set. */
export const DEADLINE_REMINDER_GENERATOR = "deadline_alert";
export const DRAFT_FOLLOW_UP_LABEL = "Draft follow-up";

export type DeadlineReminderCard = {
  /** The deadline's notification identity (`calendarInsightIdentity`): one reminder per deadline. */
  identity: string;
  /** The deadline's calendar-store item, or "" for a deadline known only by title and day. */
  itemId: string;
  title: string;
  /** All-day: local midnight of the deadline's day. Timed: the deadline itself. */
  deadlineTimestamp: number;
  /** "YYYY-MM-DD" of an all-day deadline, or null for a timed one. */
  dateKey: string | null;
};

export type FollowUpDraftCard = {
  /** The thread's notification identity: one draft per thread. */
  identity: string;
  threadId: string;
  /** The sent email's stable chunk key, on rows written since drafts were offered. */
  segmentId: string;
  /** The sent email's id, the fallback lookup for older rows. */
  emailId: string;
  subject: string;
  recipientName: string;
  sentAt: number | null;
};

export type InsightActionRequest = { notificationId: string };
export type SetDeadlineReminderRequest = InsightActionRequest & { undo?: boolean };

export type InsightActionErrorCode =
  | "unauthorized"
  | "invalid_request"
  | "invalid_json"
  | "request_too_large"
  | "not_found"
  | "suggestion_expired"
  | "too_late"
  | "no_recipient"
  | "email_unavailable"
  | "store_unavailable"
  | "internal_error";

export type SetDeadlineReminderResponse =
  | { success: true; reminderId: string; remindAt: number; alreadySet: boolean }
  | { success: true; removed: boolean }
  | { success: false; error: InsightActionErrorCode };

export type DraftFollowUpResponse =
  | { success: true; draftId: string; created: boolean }
  | { success: false; error: InsightActionErrorCode };

type UnknownRecord = Record<string, unknown>;

const record = (value: unknown): UnknownRecord | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as UnknownRecord) : null;

const text = (value: unknown) => (typeof value === "string" ? value.trim() : "");

const positive = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;

const DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

function insightFields(item: unknown, type: string): UnknownRecord | null {
  const row = record(item);
  if (!row) return null;
  const fields = { ...record(row.extraFields), ...row };
  if (text(fields.proactiveInsightType) !== type && text(fields.source) !== type) return null;
  return fields;
}

/** The deadline this row offers a reminder for, or null when the row is not one or cannot name it. */
export function readDeadlineReminderCard(item: unknown): DeadlineReminderCard | null {
  const fields = insightFields(item, "deadline_alert");
  if (!fields || text(fields.suggestedActionType) !== "set_reminder") return null;
  const identity = calendarInsightIdentity("deadline_alert", fields);
  const deadlineTimestamp = positive(fields.deadlineTimestamp);
  if (!identity || deadlineTimestamp === null) return null;
  const dateKey = text(fields.dateKey);
  return {
    identity,
    itemId: text(fields.itemId) || text(fields.eventId),
    title: text(fields.eventTitle) || "Deadline",
    deadlineTimestamp,
    dateKey: fields.isAllDay !== false && DATE_KEY_RE.test(dateKey) ? dateKey : null,
  };
}

/** The unanswered thread this row offers to draft a follow-up for, or null. */
export function readFollowUpDraftCard(item: unknown): FollowUpDraftCard | null {
  const fields = insightFields(item, "follow_up_tracker");
  if (!fields || text(fields.suggestedActionType) !== "send_follow_up") return null;
  const identity = calendarInsightIdentity("follow_up_tracker", fields);
  const threadId = text(fields.threadId);
  const emailId = text(fields.emailId);
  if (!identity || !(threadId || emailId)) return null;
  return {
    identity,
    threadId,
    segmentId: text(fields.segmentId),
    emailId,
    subject: text(fields.subject),
    recipientName: text(fields.recipientName),
    sentAt: positive(fields.sentAt),
  };
}

/** The reminder fields that say which deadline a reminder serves (`deadlineReminderDocument`). */
export type DeadlineReminderRecord = {
  id?: unknown;
  startTime?: unknown;
  isDeleted?: unknown;
  metadata?: unknown;
};

/** A reminder's metadata, stored as an object or as JSON text. */
export function deadlineReminderMetadata(reminder: DeadlineReminderRecord | null | undefined): UnknownRecord | null {
  const metadata = reminder?.metadata;
  if (typeof metadata !== "string") return record(metadata);
  try {
    return record(JSON.parse(metadata));
  } catch {
    return null;
  }
}

/** A reminder a deadline alert's verb set, for any deadline. */
export function isDeadlineReminder(reminder: DeadlineReminderRecord | null | undefined): boolean {
  return deadlineReminderMetadata(reminder)?.generator === DEADLINE_REMINDER_GENERATOR;
}

/**
 * Whether this reminder still covers this deadline: set by its alert, for the
 * deadline's current date, and not yet gone off. A reminder set before the
 * deadline moved serves the old date, so the alert asks again and a tap moves
 * the reminder. One that has gone off has done its work: it no longer hides
 * the alert, and a tap sets a new one when there is still time.
 */
export function reminderServesDeadline(
  reminder: DeadlineReminderRecord | null | undefined,
  deadline: Pick<DeadlineReminderCard, "identity" | "deadlineTimestamp" | "dateKey">,
  now: number,
): boolean {
  if (!reminder || reminder.isDeleted === true) return false;
  const metadata = deadlineReminderMetadata(reminder);
  if (metadata?.generator !== DEADLINE_REMINDER_GENERATOR || metadata.deadlineIdentity !== deadline.identity) return false;
  const storedDay = text(metadata.deadlineDateKey) || null;
  const sameDate = deadline.dateKey !== null
    ? storedDay === deadline.dateKey
    : storedDay === null && metadata.deadlineTimestamp === deadline.deadlineTimestamp;
  const remindAt = positive(reminder.startTime);
  return sameDate && remindAt !== null && remindAt > now;
}
